import React, { useState, useEffect } from 'react';
import {
    Box,
    VStack,
    HStack,
    Button,
    Text,
    Heading,
    Textarea,
    IconButton,
    Divider,
    Badge,
    Card,
    CardBody,
    useColorModeValue,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
} from '@chakra-ui/react';
import { DragHandleIcon, AddIcon, DeleteIcon } from '@chakra-ui/icons';
import { useToast } from '../hooks/useToast';
import getCSRFtoken from '../utils/getCSRFtoken';
import { spacing } from '../theme/designSystem';
import LoadingSpinner from './shared/LoadingSpinner';
import InfoMessage from './shared/InfoMessage';
import EmptyState from './shared/EmptyState';
import fetchWithAuth from '../utils/fetchWithAuth';

/**
 * SurveyPageDesigner
 *
 * Visual drag-and-drop interface for organizing survey items into pages.
 * Items can be:
 * - Questions (created separately in question editor)
 * - Text Blocks (instructional text, headers)
 * - Page Breaks (visual separators between pages)
 */
function SurveyPageDesigner({ pollId }) {
    const [items, setItems] = useState([]);
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [draggingIndex, setDraggingIndex] = useState(null);
    const toast = useToast();
    const csrfToken = getCSRFtoken();

    const cardBgColor = useColorModeValue('background.shade2');
    const borderColor = useColorModeValue('gray.300', 'gray.600');
    const dragOverColor = useColorModeValue('blue.100', 'blue.700');

    useEffect(() => {
        if (pollId) {
            fetchData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pollId]);

    const fetchData = async () => {
        try {
            setLoading(true);

            // Fetch existing survey items
            const itemsResponse = await fetchWithAuth(`/api/survey-items/?poll=${pollId}`);
            const itemsData = await itemsResponse.json();

            // Fetch all questions for this poll (to add new ones)
            const questionsResponse = await fetchWithAuth(`/api/questions/?poll=${pollId}`);
            const questionsData = await questionsResponse.json();

            setItems(itemsData.results || []);
            setQuestions(questionsData.results || []);
        } catch (error) {
            toast.showError('Error', 'Failed to load survey data');
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    const addTextBlock = async () => {
        try {
            const newOrder = items.length;
            const response = await fetchWithAuth('/api/survey-items/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': csrfToken,
                },
                body: JSON.stringify({
                    poll: pollId,
                    item_type: 'text_block',
                    order: newOrder,
                    text_content: 'Enter instructional text here...',
                }),
            });

            if (!response.ok) throw new Error('Failed to create text block');

            await fetchData();
            toast.showSuccess('Success', 'Text block added');
        } catch (error) {
            toast.showError('Error', 'Failed to add text block');
        }
    };

    const addPageBreak = async () => {
        try {
            const newOrder = items.length;
            const response = await fetchWithAuth('/api/survey-items/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': csrfToken,
                },
                body: JSON.stringify({
                    poll: pollId,
                    item_type: 'page_break',
                    order: newOrder,
                }),
            });

            if (!response.ok) throw new Error('Failed to create page break');

            await fetchData();
            toast.showSuccess('Success', 'Page break added');
        } catch (error) {
            toast.showError('Error', 'Failed to add page break');
        }
    };

    const addQuestion = async (questionId) => {
        try {
            // Check if question is already in the survey
            if (items.some(item => item.question === questionId)) {
                toast.showError('Error', 'Question already added to survey');
                return;
            }

            const newOrder = items.length;
            const response = await fetchWithAuth('/api/survey-items/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': csrfToken,
                },
                body: JSON.stringify({
                    poll: pollId,
                    item_type: 'question',
                    order: newOrder,
                    question: questionId,
                }),
            });

            if (!response.ok) throw new Error('Failed to add question');

            await fetchData();
            toast.showSuccess('Success', 'Question added');
        } catch (error) {
            toast.showError('Error', 'Failed to add question');
        }
    };

    const updateTextContent = async (itemId, newText) => {
        // Update local state immediately for responsive UI
        setItems(items.map(item =>
            item.id === itemId ? { ...item, text_content: newText } : item
        ));

        // Debounce the API call - only save after user stops typing
        if (window.textUpdateTimeout) {
            clearTimeout(window.textUpdateTimeout);
        }

        window.textUpdateTimeout = setTimeout(async () => {
            try {
                const response = await fetchWithAuth(`/api/survey-items/${itemId}/`, {
                    method: 'PATCH',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRFToken': csrfToken,
                    },
                    body: JSON.stringify({
                        text_content: newText,
                    }),
                });

                if (!response.ok) throw new Error('Failed to update text');
            } catch (error) {
                toast.showError('Error', 'Failed to save text');
            }
        }, 500); // Wait 500ms after user stops typing
    };

    const deleteItem = async (itemId) => {
        try {
            const response = await fetchWithAuth(`/api/survey-items/${itemId}/`, {
                method: 'DELETE',
                headers: {
                    'X-CSRFToken': csrfToken,
                },
            });

            if (!response.ok) throw new Error('Failed to delete item');

            await fetchData();
            toast.showSuccess('Success', 'Item deleted');
        } catch (error) {
            toast.showError('Error', 'Failed to delete item');
        }
    };

    const handleDragStart = (e, index) => {
        setDraggingIndex(index);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e, index) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    const handleDrop = async (e, dropIndex) => {
        e.preventDefault();

        if (draggingIndex === null || draggingIndex === dropIndex) {
            setDraggingIndex(null);
            return;
        }

        // Reorder items array
        const newItems = [...items];
        const [draggedItem] = newItems.splice(draggingIndex, 1);
        newItems.splice(dropIndex, 0, draggedItem);

        // Update order values
        const itemsWithNewOrder = newItems.map((item, index) => ({
            id: item.id,
            order: index,
        }));

        setItems(newItems);
        setDraggingIndex(null);

        // Save to backend
        try {
            const response = await fetchWithAuth(`/api/polls/${pollId}/reorder-items/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': csrfToken,
                },
                body: JSON.stringify({
                    items: itemsWithNewOrder,
                }),
            });

            if (!response.ok) throw new Error('Failed to reorder items');
        } catch (error) {
            toast.showError('Error', 'Failed to save new order');
            // Revert on error
            await fetchData();
        }
    };

    const getPageGroups = () => {
        const pages = [];
        let currentPage = [];

        items.forEach((item, index) => {
            if (item.item_type === 'page_break') {
                if (currentPage.length > 0) {
                    pages.push(currentPage);
                }
                pages.push([item]); // Page break as its own "page"
                currentPage = [];
            } else {
                currentPage.push(item);
            }
        });

        if (currentPage.length > 0) {
            pages.push(currentPage);
        }

        return pages;
    };

    const renderItem = (item, index) => {
        if (item.item_type === 'page_break') {
            return (
                <Box
                    key={item.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDrop={(e) => handleDrop(e, index)}
                    bg={draggingIndex === index ? dragOverColor : 'gray.100'}
                    p={4}
                    borderRadius="md"
                    borderWidth="2px"
                    borderStyle="dashed"
                    borderColor={borderColor}
                    cursor="move"
                    position="relative"
                >
                    <HStack justify="space-between">
                        <HStack>
                            <DragHandleIcon />
                            <Text fontWeight="bold" fontSize="lg">━━━ Page Break ━━━</Text>
                            <Badge colorScheme="purple">Page {getPageNumber(index)}</Badge>
                        </HStack>
                        <IconButton
                            icon={<DeleteIcon />}
                            size="sm"
                            colorScheme="red"
                            variant="ghost"
                            onClick={() => deleteItem(item.id)}
                            aria-label="Delete page break"
                        />
                    </HStack>
                </Box>
            );
        }

        if (item.item_type === 'text_block') {
            return (
                <Card
                    key={item.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDrop={(e) => handleDrop(e, index)}
                    bg={draggingIndex === index ? dragOverColor : cardBgColor}
                    borderWidth="1px"
                    borderColor={borderColor}
                    cursor="move"
                >
                    <CardBody>
                        <HStack align="start" spacing={3}>
                            <DragHandleIcon mt={2} />
                            <VStack flex={1} align="stretch" spacing={2}>
                                <HStack justify="space-between">
                                    <Badge colorScheme="blue">Text Block</Badge>
                                    <IconButton
                                        icon={<DeleteIcon />}
                                        size="sm"
                                        colorScheme="red"
                                        variant="ghost"
                                        onClick={() => deleteItem(item.id)}
                                        aria-label="Delete text block"
                                    />
                                </HStack>
                                <Textarea
                                    value={item.text_content || ''}
                                    onChange={(e) => updateTextContent(item.id, e.target.value)}
                                    placeholder="Enter instructional text..."
                                    rows={3}
                                />
                            </VStack>
                        </HStack>
                    </CardBody>
                </Card>
            );
        }

        if (item.item_type === 'question' && item.question_data) {
            return (
                <Card
                    key={item.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDrop={(e) => handleDrop(e, index)}
                    bg={draggingIndex === index ? dragOverColor : cardBgColor}
                    borderWidth="1px"
                    borderColor={borderColor}
                    cursor="move"
                >
                    <CardBody>
                        <HStack align="start" spacing={3}>
                            <DragHandleIcon mt={2} />
                            <VStack flex={1} align="stretch" spacing={2}>
                                <HStack justify="space-between">
                                    <Badge colorScheme="teal">Question</Badge>
                                    <IconButton
                                        icon={<DeleteIcon />}
                                        size="sm"
                                        colorScheme="red"
                                        variant="ghost"
                                        onClick={() => deleteItem(item.id)}
                                        aria-label="Delete question"
                                    />
                                </HStack>
                                <Text fontWeight="bold">{item.question_data.text}</Text>
                                <Text fontSize="sm" color="gray.600">
                                    {item.question_data.choices?.length || 0} choices
                                </Text>
                            </VStack>
                        </HStack>
                    </CardBody>
                </Card>
            );
        }

        return null;
    };

    const getPageNumber = (itemIndex) => {
        let pageNum = 1;
        for (let i = 0; i < itemIndex; i++) {
            if (items[i].item_type === 'page_break') {
                pageNum++;
            }
        }
        return pageNum;
    };

    const getUnusedQuestions = () => {
        const usedQuestionIds = items
            .filter(item => item.item_type === 'question')
            .map(item => item.question);
        return questions.filter(q => !usedQuestionIds.includes(q.id));
    };

    if (loading) {
        return <LoadingSpinner minHeight="400px" />;
    }

    const unusedQuestions = getUnusedQuestions();

    return (
        <Box bg={cardBgColor} p={6} borderRadius="md" border="1px solid" borderColor={borderColor}>
            <VStack spacing={spacing.group} align="stretch">
                {/* Header */}
                <Box>
                    <Heading as="h3" size="md" mb={1}>
                        Organize Survey Pages
                    </Heading>
                    <Text fontSize="xs" color="gray.600">
                        Drag to reorder • Add page breaks to group questions together
                    </Text>
                </Box>

                <Divider />

                {/* Add Items Menu */}
                <HStack spacing={spacing.compact} wrap="wrap">
                    <Menu>
                        <MenuButton as={Button} leftIcon={<AddIcon />} size="sm" colorScheme="teal">
                            Add Question
                        </MenuButton>
                        <MenuList maxH="300px" overflowY="auto">
                            {unusedQuestions.length === 0 ? (
                                <MenuItem isDisabled>No unused questions</MenuItem>
                            ) : (
                                unusedQuestions.map(q => (
                                    <MenuItem key={q.id} onClick={() => addQuestion(q.id)}>
                                        {q.text}
                                    </MenuItem>
                                ))
                            )}
                        </MenuList>
                    </Menu>
                    <Button leftIcon={<AddIcon />} onClick={addTextBlock} size="sm" colorScheme="blue">
                        Add Text Block
                    </Button>
                    <Button leftIcon={<AddIcon />} onClick={addPageBreak} size="sm" colorScheme="purple">
                        Add Page Break
                    </Button>
                </HStack>

                {/* Survey Items List */}
                {items.length === 0 ? (
                    <EmptyState message="No items yet. Add questions, text blocks, or page breaks to build your survey." />
                ) : (
                    <>
                        <VStack spacing={spacing.compact} align="stretch">
                            {items.map((item, index) => renderItem(item, index))}
                        </VStack>

                        {/* Page Preview Info */}
                        <InfoMessage>
                            Your survey has <strong>{getPageGroups().length} page(s)</strong>.
                            Items between page breaks appear together.
                        </InfoMessage>
                    </>
                )}
            </VStack>
        </Box>
    );
}

export default SurveyPageDesigner;
